import type { CreateCustomerRequest, Customer, InvoiceDetail } from "@stb/api-contract"
import { Link } from "@tanstack/react-router"
import { AlertTriangleIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react"
import { useState } from "react"

import { Alert, AlertDescription } from "@/ui/alert"
import { Badge } from "@/ui/badge"
import { Button } from "@/ui/button"
import { Card } from "@/ui/card"
import { Muted } from "@/ui/typography"
import { cn } from "@/ui/utils"

import { formatAud } from "../../format"
import type { RpcResult } from "../../rpc/context"
import { ConfirmDeleteCustomer } from "../confirm-delete-customer/confirm-delete-customer"
import { CustomerFormDialog } from "../customer-form-dialog/customer-form-dialog"

export interface CustomerListProps {
  customers: Customer[]
  /** All invoices, to total what each customer owes. */
  invoices: InvoiceDetail[]
  /** `null` id creates. */
  onSubmitCustomer: (
    id: string | null,
    request: CreateCustomerRequest,
  ) => Promise<RpcResult<Customer>>
  onDelete: (id: string) => Promise<RpcResult<null>>
  /** The server changed: refetch the page. */
  onChanged: () => void
}

export function customerStats(invoices: InvoiceDetail[], customerId: string) {
  const mine = invoices.filter((invoice) => invoice.customer_id === customerId)
  return {
    count: mine.length,
    owing: mine.reduce((total, invoice) => total + invoice.outstanding_minor, 0),
    overdue: mine.some((invoice) => invoice.display_status === "overdue"),
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join("")
}

/** Customers as cards (PRD R2.1): Edit and Delete are always visible, so the keyboard reaches them. */
export function CustomerList({
  customers,
  invoices,
  onSubmitCustomer,
  onDelete,
  onChanged,
}: CustomerListProps) {
  const [editing, setEditing] = useState<Customer | "new" | null>(null)
  const [deleting, setDeleting] = useState<Customer | null>(null)

  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
          <Muted>
            {customers.length} {customers.length === 1 ? "customer" : "customers"}
          </Muted>
        </div>
        <Button onClick={() => setEditing("new")}>
          <PlusIcon aria-hidden="true" />
          New customer
        </Button>
      </header>

      {customers.length === 0 ? (
        <Card className="items-center gap-3 px-6 py-12 text-center">
          <h2 className="text-lg font-semibold">No customers yet</h2>
          <Muted className="max-w-sm">
            Add the people and businesses you invoice. A name is enough to start.
          </Muted>
          <Button onClick={() => setEditing("new")}>
            <PlusIcon aria-hidden="true" />
            New customer
          </Button>
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {customers.map((customer) => {
            const stats = customerStats(invoices, customer.id)
            return (
              <li key={customer.id}>
                <Card
                  className={cn(
                    "gap-3 px-5 py-4",
                    stats.overdue && "border-l-destructive border-l-4",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-medium"
                    >
                      {initials(customer.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/customers/$id"
                        params={{ id: customer.id }}
                        className="font-medium underline-offset-4 hover:underline"
                      >
                        {customer.name}
                      </Link>
                      {customer.email && (
                        <p className="text-muted-foreground truncate text-sm">{customer.email}</p>
                      )}
                    </div>
                    {stats.overdue && <Badge variant="destructive">Overdue</Badge>}
                  </div>
                  <dl className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <dt className="text-muted-foreground text-xs">ABN</dt>
                      <dd>{customer.abn ?? "No ABN"}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">Invoices</dt>
                      <dd>{stats.count}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-xs">Owing</dt>
                      <dd
                        className={cn(
                          "tabular-nums",
                          stats.overdue && "text-destructive font-medium",
                        )}
                      >
                        {formatAud(stats.owing)}
                      </dd>
                    </div>
                  </dl>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`Edit ${customer.name}`}
                      onClick={() => setEditing(customer)}
                    >
                      <PencilIcon aria-hidden="true" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Delete ${customer.name}`}
                      onClick={() => setDeleting(customer)}
                    >
                      <Trash2Icon aria-hidden="true" />
                      Delete
                    </Button>
                  </div>
                </Card>
              </li>
            )
          })}
        </ul>
      )}

      {editing && (
        <CustomerFormDialog
          open
          onOpenChange={(next) => !next && setEditing(null)}
          customer={editing === "new" ? undefined : editing}
          onSubmit={(request) => onSubmitCustomer(editing === "new" ? null : editing.id, request)}
          onDone={() => {
            setEditing(null)
            onChanged()
          }}
        />
      )}
      {deleting && (
        <ConfirmDeleteCustomer
          customer={deleting}
          onCancel={() => setDeleting(null)}
          onDelete={onDelete}
          onDone={() => {
            setDeleting(null)
            onChanged()
          }}
        />
      )}
    </main>
  )
}

export function CustomerListSkeleton() {
  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
      <div
        role="status"
        aria-label="Loading customers"
        aria-busy="true"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        {[0, 1, 2].map((card) => (
          <Card key={card} className="gap-3 px-5 py-4">
            <div className="bg-muted h-5 w-1/2 animate-pulse rounded motion-reduce:animate-none" />
            <div className="bg-muted h-4 w-3/4 animate-pulse rounded motion-reduce:animate-none" />
          </Card>
        ))}
      </div>
    </main>
  )
}

export function CustomerListError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="mx-auto w-full max-w-6xl space-y-6 px-6 py-8">
      <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
      <Alert variant="destructive">
        <AlertTriangleIcon aria-hidden="true" />
        <AlertDescription>
          <p>{message}</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    </main>
  )
}
